import * as Sentry from '@sentry/browser';
import { isPlainObject, omit } from 'lodash-es';
import posthog from 'posthog-js';
import { useCallback, useMemo, useRef, useState } from 'react';

import { areAnalyticsEnabledInEnv } from '../areAnalyticsEnabledInEnv';
import useQueueUntilReady from '../utils/useQueueUntilReady';
import type { Integration, PageViewProps, UserData } from './types';

const postHogUrl = 'https://eu.i.posthog.com';

// Effectively same as in `apolloLoggingPlugin`
const SENSITIVE_VARIABLES = [
  'password',
  'auth',
  'authentication',
  'currentPassword',
  'newPassword',
  'token',
  'loginToken',
];

class MaskCapturedNetworkRequestError extends Error {
  constructor(error: unknown) {
    super(
      `Something went wrong when running maskCapturedNetworkRequestFn: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

const redactSensitiveData = (data: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]): [string, unknown] => {
      if (SENSITIVE_VARIABLES.includes(key)) {
        return [key, 'REDACTED'];
      }

      if (isPlainObject(value)) {
        return [key, redactSensitiveData(value as Record<string, unknown>)];
      }

      return [key, value];
    }),
  );

const usePostHogIntegration = (): Integration => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationState = useRef<'idle' | 'loading' | 'done'>('idle');
  const enabled = isPostHogEnabled();

  const trackEvent = useCallback((eventName: string, props: Record<string, unknown>) => {
    if (!isPostHogEnabled()) return;

    posthog.capture(eventName, props);
  }, []);
  const initAwareTrackEvent = useQueueUntilReady(trackEvent, isInitialized);

  const trackPageView = useCallback(({ path }: PageViewProps) => {
    if (!isPostHogEnabled()) return;

    posthog.capture('$pageview', { path });
  }, []);
  const initAwareTrackPageView = useQueueUntilReady(trackPageView, isInitialized);

  const identifyUser = useCallback(({ commonData: { id, fullName, ...commonData } }: UserData) => {
    if (!isPostHogEnabled()) return;

    posthog.identify(id, {
      ...omit(commonData, 'company'),
      name: fullName,
      // In PostHog session replay, it's not possible to filter by group properties or nested properties.
      // Therefore, we need to flatten the company data and add it as top-level properties.
      companyId: commonData.company.id,
      companyName: commonData.company.name,
      companyType: commonData.company.type,
      companyPlan: commonData.company.plan,
    });
    posthog.group('company', commonData.company.id, commonData.company);
  }, []);
  const initAwareIdentifyUser = useQueueUntilReady(identifyUser, isInitialized);

  const resetUserIdentity = useCallback(() => {
    if (!isPostHogEnabled()) return;

    posthog.reset();
  }, []);

  const initialize = useCallback(() => {
    if (!isPostHogEnabled() || !process.env.POSTHOG_API_KEY) return;

    if (initializationState.current !== 'idle') return;
    initializationState.current = 'loading';

    const apiKey = process.env.POSTHOG_API_KEY;

    return new Promise<void>((resolve) => {
      posthog.init(apiKey, {
        api_host: postHogUrl,
        person_profiles: 'always',
        session_recording: {
          maskCapturedNetworkRequestFn: (request) => {
            if (request.name.includes('/internal/auth/')) return null;

            if (request.name.includes('/internal/graphql') && request.requestBody) {
              try {
                const parsedBody = JSON.parse(request.requestBody) as Record<string, unknown>;
                const parsedResponse =
                  request.responseBody &&
                  (JSON.parse(request.responseBody) as Record<string, unknown> | null | undefined);

                if (parsedBody.variables && isPlainObject(parsedBody.variables)) {
                  parsedBody.variables = redactSensitiveData(
                    parsedBody.variables as Record<string, unknown>,
                  );
                }

                if (parsedResponse && parsedResponse.data && isPlainObject(parsedResponse.data)) {
                  parsedResponse.data = redactSensitiveData(
                    parsedResponse.data as Record<string, unknown>,
                  );
                }

                request.requestBody = JSON.stringify(parsedBody);
                request.responseBody = JSON.stringify(parsedResponse);
              } catch (error) {
                Sentry.captureException(new MaskCapturedNetworkRequestError(error));
                return null;
              }
            }

            return request;
          },
        },
        loaded: () => {
          initializationState.current = 'done';
          setIsInitialized(true);
          resolve();
        },
      });
    });
  }, []);

  return useMemo(
    () => ({
      name: 'postHog',
      enabled,
      initialize,
      trackEvent: initAwareTrackEvent,
      trackPageView: initAwareTrackPageView,
      identifyUser: initAwareIdentifyUser,
      resetUserIdentity,
    }),
    [
      enabled,
      initialize,
      initAwareTrackEvent,
      initAwareTrackPageView,
      initAwareIdentifyUser,
      resetUserIdentity,
    ],
  );
};

const isPostHogEnabled = () =>
  areAnalyticsEnabledInEnv() &&
  Boolean(process.env.POSTHOG_API_KEY) &&
  process.env.REACT_APP_CYPRESS !== 'true';

export default usePostHogIntegration;
